import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { ajaxGetProductsAutocomplete } from '../../services/productService';
import { ajaxGetSalesBomCost } from '../../services/reportsService';
import StyledSearchDate from '../../components/StyledSearchDate';
import useAlert from '../../hooks/useAlert'
import moment from 'moment';
import Chip from '@mui/material/Chip';
import LoadingButton from '../../components/LoadingButton';

export default function SalesBomCost() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [readyToDownload, setReadyToDownload] = useState(false);
  const errorAlert = useAlert(false, 'error');

  useEffect(() => {
    ajaxGetProductsAutocomplete().then((res) => {
      setProducts(res.data);
    }).catch(() => {
      setSelectedProducts([]);
    });
  }, []);

  useEffect(() => {
    setReadyToDownload(startDate !== null && endDate !== null && (selectAll || selectedProducts.length > 0));
  }, [startDate, endDate, selectedProducts, selectAll]);

  const handleProductSelection = (_, value) => {
    setSelectedProducts(value);
    setSelectAll(false);
  };

  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedProducts([]);
    }
  };

  const handleDownload = (format) => () => {
    errorAlert.resetAlert();
    setIsProcessing(true);

    let filename = `sales-bom-cost_${moment.unix(startDate).format('YYYY-MM-DD')}_${moment.unix(endDate).format('YYYY-MM-DD')}.${format}`;
    filename = filename.replace(/[\s/\\?%*:|"<>]/g, '');

    return ajaxGetSalesBomCost(
      {
        format: format,
        productId: selectAll ? products.map(p => p.id) : selectedProducts.map(p => p.id),
        startDate: startDate,
        endDate: endDate,
      },
      filename
    )
    .then(() => {
      setIsProcessing(false);
    })
    .catch((e) => {
      errorAlert.setAlert(e.message);
      setIsProcessing(false);
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAllChange}
              disabled={isProcessing}
            />
          }
          label="All Products"
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          disablePortal
          fullWidth
          size="small"
          value={selectedProducts}
          options={products}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `[${option.product_code}] ${option.desc}`}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.product_code}
                {...getTagProps({ index })}
              />
            ))
          }
          onChange={handleProductSelection}
          disabled={selectAll || isProcessing}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Select Products" 
              placeholder="Select one or more products"
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledSearchDate
          startDate={startDate}
          endDate={endDate}
          onStartDate={setStartDate}
          onEndDate={setEndDate}
          gridProps={{ columnSpacing: 1 }}
        />
      </Grid>

      <Grid item xs={12}>
        <LoadingButton
          size="small"
          variant="contained"
          color="info"
          sx={{ margin: '10px 20px 10px 0' }}
          onClickPromise={handleDownload('pdf')}
          disabled={!readyToDownload}
        >
          Download in PDF
        </LoadingButton>

        <LoadingButton
          size="small"
          variant="contained"
          color="info"
          sx={{ margin: '10px 0' }}
          onClickPromise={handleDownload('csv')}
          disabled={!readyToDownload}
        >
          Download in CSV
        </LoadingButton>
      </Grid>
    </Grid>
  );
} 